// The API's public URL is baked in at build time (create-react-app inlines REACT_APP_*
// variables): set it with the REACT_APP_API_BASE_URL build argument (see the Dockerfile).
// The localhost fallback is only for running the dev server.
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/';

// Endpoints whose job is to establish who you are. Sending the credentials we happen to be holding
// makes no sense for these, and a stale one used to make them fail outright: a browser left holding
// the credentials of a deleted account got a 401 from sign-up, so the one action that would have
// replaced them was the one thing it could not do (2026-09-22).
const ESTABLISHES_IDENTITY = [
    'user/create/',
    'token/obtain/',
    'token/refresh/',
    'user/password-reset/',
    'user/password-reset/confirm/',
];

function needsNoCredentials(urlExtension) {
    return ESTABLISHES_IDENTITY.includes(urlExtension);
}

export const AuthService = {
    
    async refreshTokensData() {
        const tokens = JSON.parse(sessionStorage.getItem('tokens'));

        if (!tokens) return false;

        try {
            const response = await fetch(baseUrl + 'token/refresh/', {
                method: 'POST', // *GET, POST, PUT, DELETE, etc.
                mode: 'cors', // no-cors, *cors, same-origin
                cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
                credentials: 'include', // include, *same-origin, omit
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                },
                redirect: 'follow', // manual, *follow, error
                body: JSON.stringify(tokens),
            });

            if (response.ok) {
                const newTokens = await response.json();
                const existingTokens = JSON.parse(sessionStorage.getItem('tokens')) || {};
                const updatedTokens = { ...existingTokens, ...newTokens };
                sessionStorage.setItem('tokens', JSON.stringify(updatedTokens));
                return true;
            }

            // The API has refused them outright (expired, blacklisted, or the account is gone). Throw
            // them away rather than sending them with every later request, which only produces more
            // failures. A 5xx is a different matter: the credentials may well be fine, so they stay.
            if (response.status === 401 || response.status === 403) {
                sessionStorage.removeItem('tokens');
            }

            return false;


        } catch (error) {
            console.error(`[AuthService] ${error}`);
            return false;
        }
    },

    async makeRequestData({ urlExtension, method, body, retrying = false, stream = false }) {
        const tokens = JSON.parse(sessionStorage.getItem('tokens'));
        const access = needsNoCredentials(urlExtension) ? null : tokens?.access;

        const response = await fetch(baseUrl + urlExtension, {
            method,
            headers: {
                'Authorization': access ? `JWT ${access}` : undefined,
                'Content-Type': 'application/json',
                'accept': 'application/json',
            },
            body: body ? JSON.stringify(body) : null,
            credentials: 'include',
        });

        // Handle 401 (unauthorized) once, if not already retried. Not worth doing for the endpoints
        // above: they sent no credentials, so refreshing cannot change the answer.
        if (response.status === 401 && !retrying && !needsNoCredentials(urlExtension)) {
            console.warn(`[AuthService] Received 401 — trying to refresh token...`);
            const refreshed = await this.refreshTokens();
            if (refreshed) {
                // Retry original request once after refreshing
                return this.makeRequestData({ urlExtension, method, body, retrying: true, stream });
            } else {
                console.error(`[AuthService] Token refresh failed`);
                return { data: null, error: true };
            }
        }

        // If streaming, return the raw stream immediately
        if (stream && response.ok && response.body) {
            return { data: response.body, error: false, streamed: true };
        }

        // Non-streaming json response
        let json = null;
        try {
            json = await response.json();
        } catch {}

        return {
            data: response.ok ? json : json,
            error: !response.ok,
            streamed: false,
        };
    },

    refreshTokens() {
        return this.refreshTokensData();
    },

    makeRequest({ urlExtension, method, body, stream = false }) {
        return this.makeRequestData({ urlExtension, method, body, stream });
    },

}